import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../flux/stores/hooks';
import { fetchProjects, fetchMyProjects, createProject } from '../../flux/actions/projectsThunks';
import { clearMutationError } from '../../flux/stores/projectsReducer';
import { useApiAccessToken } from '../../utils/useApiAccessToken';
import { CreateProjectDialog } from './CreateProjectDialog';
import { ApsLinkCell } from './ApsLinkCell';

function formatDate(value) {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
}

export const Projects = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { getAccessToken } = useApiAccessToken();

    const currentUser = useAppSelector(state => state.user.current.data);
    const userLoading = useAppSelector(state => state.user.current.isLoading);
    const userError = useAppSelector(state => state.user.current.error);
    const projects = useAppSelector(state => state.projects.list.data);
    const isLoading = useAppSelector(state => state.projects.list.isLoading);
    const listError = useAppSelector(state => state.projects.list.error);
    const mutation = useAppSelector(state => state.projects.mutation);

    const [isCreateOpen, setCreateOpen] = useState(false);

    const isAdmin = currentUser?.org_role === 'admin';

    // An admin gets every project the org owns, which is what they can act on.
    // Everyone else gets the projects they hold a membership row for. These are
    // genuinely different questions, not one list filtered two ways.
    const loadProjects = useCallback(async () => {
        if (!currentUser) return;
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return;
            dispatch(isAdmin ? fetchProjects({ accessToken }) : fetchMyProjects({ accessToken }));
        } catch (error) {
            console.error('Failed to acquire API token for projects:', error);
        }
    }, [currentUser, dispatch, getAccessToken, isAdmin]);

    useEffect(() => {
        loadProjects();
    }, [loadProjects]);

    const openCreate = () => {
        dispatch(clearMutationError());
        setCreateOpen(true);
    };

    const handleCreate = async (name) => {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return;
            const result = await dispatch(createProject({ accessToken, name }));
            if (createProject.fulfilled.match(result)) {
                setCreateOpen(false);
                navigate(`/projects/${result.payload.object_id}`);
            }
        } catch (error) {
            console.error('Failed to create the project:', error);
        }
    };

    // Which list to load depends on the caller's org role, so nothing can be
    // fetched or rendered until `/me` has answered. Showing the page early
    // would flash the non-admin empty state at an admin.
    if (!currentUser) {
        return (
            <div className="page-content">
                {userError && !userLoading
                    ? <p className="org-error">Your account could not be loaded. Reload the page to try again.</p>
                    : <p className="projects-loading">Loading…</p>}
            </div>
        );
    }

    return (
        <div className="page-content">
            <div className="rules-header">
                <h1 className="rules-title">Projects</h1>
                {isAdmin && (
                    <button className="rules-btn-primary" onClick={openCreate}>
                        + New Project
                    </button>
                )}
            </div>

            {!isAdmin && projects.length > 0 && (
                <p className="pd-note">These are the projects you are a member of.</p>
            )}

            {listError && listError.status !== 403 && (
                <p className="org-error">{listError.message}</p>
            )}

            {isLoading && projects.length === 0 ? (
                <p className="projects-loading">Loading projects…</p>
            ) : projects.length === 0 ? (
                <div className="rules-empty">
                    <p>
                        {isAdmin
                            ? 'No projects yet. Create one to get started.'
                            : 'You are not a member of any project yet. An organization admin can invite you to one.'}
                    </p>
                </div>
            ) : (
                <div className="rules-table-wrapper">
                    <table className="rules-table">
                        <thead>
                            <tr>
                                <th className="rules-th">Name</th>
                                <th className="rules-th">Autodesk</th>
                                <th className="rules-th">Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {projects.map(project => (
                                <tr
                                    key={project.object_id}
                                    className="rules-tr projects-tr-clickable"
                                    onClick={() => navigate(`/projects/${project.object_id}`)}
                                >
                                    <td className="rules-td rules-td-name">{project.name}</td>
                                    <td className="rules-td">
                                        <ApsLinkCell projectId={project.object_id} isAdmin={isAdmin} />
                                    </td>
                                    <td className="rules-td">{formatDate(project.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {isCreateOpen && (
                <CreateProjectDialog
                    isSaving={mutation.isSaving}
                    error={mutation.error}
                    onCancel={() => setCreateOpen(false)}
                    onCreate={handleCreate}
                />
            )}
        </div>
    );
};
